import { useEffect, useState, useCallback } from "react";
import { logInfo, logWarn, logError as monLogError } from "@/lib/monitoring";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { validatePassword } from "@/lib/security";
import { clearSession, clearLocalToken } from "@/hooks/useSessionGuard";
import { triggerOfflineClear } from "@/lib/offlineSync";
import { clearRoleCache } from "@/components/RouteGuard";
import type { Session } from "@supabase/supabase-js";
import {
  type AppRole,
  LOGIN_ROUTE,
  CHANGE_PASSWORD_ROUTE,
  roleDashboard,
} from "@/lib/roles";

// ─── Types ───────────────────────────────────────────────────────────
export type { AppRole };


interface AuthState {
  session: Session | null;
  loading: boolean;
}

interface AuthActions {
  signIn: (email: string, password: string) => Promise<string>;
  signOut: () => Promise<void>;
  redirectByRole: (userId: string) => Promise<void>;
  getUserRole: (userId: string) => Promise<AppRole | null>;
}

export type UseAuthReturn = AuthState & AuthActions;

// ─── Constants ───────────────────────────────────────────────────────
// ROLE_DASHBOARD_ROUTES e LOGIN_ROUTE vivem em @/lib/roles (fonte única).


// ─── Error Messages ─────────────────────────────────────────────────
const ERROR_INACTIVE_ACCOUNT = "Conta inativa. Contate o administrador.";
const ERROR_ROLE_NOT_FOUND = "Perfil de acesso não encontrado. Contate o suporte.";

// ─── Helper Functions ───────────────────────────────────────────────
async function fetchUserRole(userId: string): Promise<AppRole | null> {
  const { data, error } = await supabase.rpc("get_user_role", {
    _user_id: userId,
  });

  if (error) {
    monLogError("Erro ao buscar role do usuário", error, { userId });
    return null;
  }

  return (data as AppRole) ?? null;
}

async function checkUserIsActive(userId: string): Promise<boolean> {
  const { data, error } = await supabase.rpc("is_user_active", {
    _user_id: userId,
  });

  if (error) {
    monLogError("Erro ao verificar status do usuário", error, { userId });
    return false;
  }

  return data === true;
}

async function checkMustChangePassword(userId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from("profiles")
    .select("must_change_password")
    .eq("user_id", userId)
    .maybeSingle();

  if (error) {
    monLogError("Erro ao verificar senha provisória", error, { userId });
    return false;
  }

  return data?.must_change_password === true;
}

// ─── Hook ────────────────────────────────────────────────────────────
export function useAuth(): UseAuthReturn {
  const [authState, setAuthState] = useState<AuthState>({
    session: null,
    loading: true,
  });
  const navigate = useNavigate();

  // Subscribe to auth state changes
  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setAuthState({ session, loading: false });

      // Handle session expiry / sign out
      if (event === "SIGNED_OUT" || (event === "TOKEN_REFRESHED" && !session)) {
        setAuthState({ session: null, loading: false });
        navigate(LOGIN_ROUTE);
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setAuthState({ session, loading: false });
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const getUserRole = useCallback(fetchUserRole, []);

  const redirectByRole = useCallback(
    async (userId: string) => {
      const role = await fetchUserRole(userId);

      const destino = roleDashboard(role);
      if (!destino) {
        logWarn("Role não encontrada para redirecionamento", { userId });
        return;
      }

      logInfo("Redirecionamento por role", { role, destino });
      navigate(destino);

    },
    [navigate]
  );

  const signIn = useCallback(
    async (email: string, password: string): Promise<string> => {
      const normalizedEmail = email.trim().toLowerCase();

      // 0. Bloqueio progressivo (Onda 5): 5 falhas em 15min = bloqueio de 15min
      try {
        const { data: blockData } = await (supabase.rpc as any)("is_email_login_blocked", {
          _email: normalizedEmail,
        });
        const block = blockData as { blocked?: boolean; retry_after_seconds?: number } | null;
        if (block?.blocked) {
          const mins = Math.ceil((block.retry_after_seconds ?? 900) / 60);
          throw new Error(
            `Muitas tentativas de login. Tente novamente em ${mins} minuto(s).`
          );
        }
      } catch (err: any) {
        // se a RPC falhar, não bloqueia o login — apenas registra
        if (err?.message?.startsWith("Muitas tentativas")) throw err;
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email: normalizedEmail,
        password,
      });

      if (error) throw error;

      const userId = data.user.id;

      // 1. Verify account is active
      const isActive = await checkUserIsActive(userId);
      if (!isActive) {
        await supabase.auth.signOut();
        throw new Error(ERROR_INACTIVE_ACCOUNT);
      }

      // 2. Check if provisional password must be changed
      const mustChange = await checkMustChangePassword(userId);
      if (mustChange) {
        navigate(CHANGE_PASSWORD_ROUTE);
        return userId;
      }

      // 3. Check if current password meets security policy (12+ chars, complexity)
      const pwdCheck = validatePassword(password);
      if (!pwdCheck.valid) {
        // Mark profile so user is forced to change
        await supabase.from("profiles").update({ must_change_password: true }).eq("user_id", userId);
        navigate(CHANGE_PASSWORD_ROUTE);
        return userId;
      }

      // 4. Redirect to role-specific dashboard
      await redirectByRole(userId);
      return userId;
    },
    [navigate, redirectByRole]
  );

  const signOut = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user?.id) {
        logInfo("Logout realizado", { userId: session.user.id });
        await clearSession(session.user.id);
      } else {
        clearLocalToken();
      }
      await supabase.auth.signOut();
      triggerOfflineClear();
      clearRoleCache();
    } catch (error) {
      monLogError("Erro ao fazer logout", error);
    } finally {
      navigate(LOGIN_ROUTE);
    }
  }, [navigate]);

  return {
    session: authState.session,
    loading: authState.loading,
    signIn,
    signOut,
    redirectByRole,
    getUserRole,
  };
}
